/**
 * Capability #5 — change → locator/selector-level automation impact.
 * Pure deterministic helpers + wire schemas. No LLM.
 */
import { z } from 'zod';

export const LOCATOR_IMPACT_EVIDENCE_LEVELS = ['DIRECT', 'STRONG', 'INDIRECT', 'UNKNOWN'] as const;
export type LocatorImpactEvidenceLevel = (typeof LOCATOR_IMPACT_EVIDENCE_LEVELS)[number];

export const LOCATOR_IMPACT_CONFIDENCE_BANDS = ['high', 'medium', 'low', 'unknown'] as const;
export type LocatorImpactConfidenceBand = (typeof LOCATOR_IMPACT_CONFIDENCE_BANDS)[number];

export const LocatorImpactGovernanceSchema = z.object({
  autoApply: z.literal(false),
  silentPublish: z.literal(false),
  recommendationsAreNotTruth: z.literal(true),
  advisoryOnly: z.literal(true),
  autoHeal: z.literal(false),
  autoMutateLocators: z.literal(false),
  autoSkip: z.literal(false),
  autoBlocksRelease: z.literal(false),
});
export type LocatorImpactGovernance = z.infer<typeof LocatorImpactGovernanceSchema>;

export const LocatorImpactFindingSchema = z.object({
  id: z.string().max(120),
  evidenceLevel: z.enum(LOCATOR_IMPACT_EVIDENCE_LEVELS),
  confidenceBand: z.enum(LOCATOR_IMPACT_CONFIDENCE_BANDS),
  reason: z.string().max(500),
  sourceFile: z.string().max(500).nullable(),
  sourceToken: z.string().max(300).nullable(),
  flowId: z.string().uuid(),
  flowName: z.string().max(300),
  stepId: z.string().max(120).nullable().optional(),
  stepTitle: z.string().max(300).nullable().optional(),
  locator: z.string().max(500).nullable(),
  matchedRoutes: z.array(z.string().max(200)).max(8).default([]),
  evidenceUsed: z.array(z.string().max(200)).max(12).default([]),
  evidenceMissing: z.array(z.string().max(200)).max(8).default([]),
});
export type LocatorImpactFinding = z.infer<typeof LocatorImpactFindingSchema>;

export const LocatorAutomationImpactSchema = z.object({
  attribution: z.literal('locator_automation_impact'),
  governance: LocatorImpactGovernanceSchema,
  summary: z.string().max(600),
  why: z.array(z.string().max(300)).max(8).default([]),
  counts: z.object({
    affectedFlows: z.number().int().nonnegative(),
    direct: z.number().int().nonnegative(),
    strong: z.number().int().nonnegative(),
    indirect: z.number().int().nonnegative(),
    unknown: z.number().int().nonnegative(),
  }),
  findings: z.array(LocatorImpactFindingSchema).max(30),
  limits: z.object({
    maxChangedFiles: z.number().int().positive(),
    maxCandidateFlows: z.number().int().positive(),
    maxFindings: z.number().int().positive(),
    maxStepsPerFlow: z.number().int().positive(),
    comparisonStrategy: z.string().max(160),
  }),
});
export type LocatorAutomationImpact = z.infer<typeof LocatorAutomationImpactSchema>;

export const LOCATOR_IMPACT_GOVERNANCE: LocatorImpactGovernance = {
  autoApply: false,
  silentPublish: false,
  recommendationsAreNotTruth: true,
  advisoryOnly: true,
  autoHeal: false,
  autoMutateLocators: false,
  autoSkip: false,
  autoBlocksRelease: false,
};

/** Normalize a locator/selector string for comparison. */
export function normalizeLocatorToken(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/^['"`]+|['"`]+$/g, '')
    .replace(/\s+/g, ' ');
}

/**
 * Extract identifiable selector / test-id tokens from source text.
 * Deterministic regex only — not a parser / AST engine.
 */
export function extractSelectorTokensFromSource(source: string): string[] {
  if (!source || typeof source !== 'string') return [];
  const out = new Set<string>();
  const patterns: RegExp[] = [
    /data-testid\s*=\s*["'`]([^"'`]+)["'`]/gi,
    /data-test-id\s*=\s*["'`]([^"'`]+)["'`]/gi,
    /data-cy\s*=\s*["'`]([^"'`]+)["'`]/gi,
    /getByTestId\s*\(\s*["'`]([^"'`]+)["'`]\s*\)/gi,
    /getByRole\s*\(\s*["'`]([^"'`]+)["'`]/gi,
    /locator\s*\(\s*["'`]([^"'`]{2,200})["'`]\s*\)/gi,
    /\[[\w-]+\s*=\s*["'`]([^"'`]+)["'`]\]/g,
    /#([A-Za-z][\w-]{1,80})/g,
  ];
  for (const re of patterns) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(source)) !== null) {
      const token = normalizeLocatorToken(m[1] ?? '');
      if (token.length >= 2) out.add(token);
    }
  }
  return [...out].sort().slice(0, 80);
}

/** Soft tokens from a file path (component / module name heuristics). */
export function extractPathTokens(filePath: string): string[] {
  const base = filePath.split(/[/\\]/).pop() ?? filePath;
  const stem = base.replace(/\.[^.]+$/, '');
  const parts = stem
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .toLowerCase()
    .split(/\s+/)
    .map((p) => p.trim())
    .filter((p) => p.length >= 3 && !['index', 'page', 'view', 'component', 'test', 'spec'].includes(p));
  return [...new Set(parts)].slice(0, 8);
}

export function locatorContainsToken(locator: string, token: string): boolean {
  const nLoc = normalizeLocatorToken(locator);
  const nTok = normalizeLocatorToken(token);
  if (!nLoc || !nTok) return false;
  if (nLoc === nTok) return true;
  // Exact attribute-value style
  if (nLoc.includes(`="${nTok}"`) || nLoc.includes(`='${nTok}'`) || nLoc.includes(`=\`${nTok}\``)) return true;
  if (nLoc.includes(`[data-testid="${nTok}"]`) || nLoc.includes(`[data-testid='${nTok}']`)) return true;
  if (nLoc.includes(nTok) && nTok.length >= 4) return true;
  return false;
}

export function scoreDirectLocatorMatch(
  automationLocators: string[],
  changeTokens: string[],
): { matchedLocator: string | null; matchedToken: string | null } {
  for (const loc of automationLocators) {
    for (const tok of changeTokens) {
      if (locatorContainsToken(loc, tok)) {
        return { matchedLocator: loc, matchedToken: tok };
      }
    }
  }
  return { matchedLocator: null, matchedToken: null };
}

export type FlowLocatorCandidate = {
  flowId: string;
  flowName: string;
  routes: string[];
  selectors: string[];
};

export type StepLocatorCandidate = {
  stepId: string;
  stepTitle: string;
  locator: string;
};

/** Extract step locators from canvas_json / draft canvas (bounded). */
export function extractStepLocatorsFromCanvas(canvas: unknown, maxSteps = 40): StepLocatorCandidate[] {
  if (!canvas || typeof canvas !== 'object') return [];
  const root = canvas as Record<string, unknown>;
  const stepsRaw = Array.isArray(root.steps)
    ? root.steps
    : Array.isArray((root as { nodes?: unknown }).nodes)
      ? (root as { nodes: unknown[] }).nodes
      : [];
  const out: StepLocatorCandidate[] = [];
  for (const raw of stepsRaw) {
    if (out.length >= maxSteps) break;
    if (!raw || typeof raw !== 'object') continue;
    const s = raw as Record<string, unknown>;
    const stepId = String(s.id ?? s.stepId ?? '');
    if (!stepId) continue;
    const stepTitle = String(s.title ?? s.name ?? s.stepTitle ?? stepId);
    const candidates: string[] = [];
    if (typeof s.target === 'string' && s.target && !/^https?:\/\//i.test(s.target) && !s.target.startsWith('/')) {
      candidates.push(s.target);
    }
    if (typeof s.promotedSelector === 'string' && s.promotedSelector) candidates.push(s.promotedSelector);
    const meta = s.targetMeta;
    if (meta && typeof meta === 'object') {
      const pm = (meta as { promotedSelector?: string }).promotedSelector;
      if (pm) candidates.push(pm);
    }
    const recorded = s.recordedCandidates;
    if (Array.isArray(recorded)) {
      for (const c of recorded) {
        if (c && typeof c === 'object' && typeof (c as { selector?: string }).selector === 'string') {
          candidates.push((c as { selector: string }).selector);
        }
      }
    }
    const locator = candidates.map((c) => c.trim()).find(Boolean) ?? null;
    if (!locator) continue;
    out.push({ stepId, stepTitle, locator });
  }
  return out;
}
